<template>
  <div class="settings-container">
    <substrate-mount flat>
      <template v-for="paymentOption in paymentOptions">
        <setting-receiving-payments-qiwi
          v-if="paymentOption.name === 'QIWI'"
          :key="paymentOption.name"
          :option="paymentOption"
          @blocked="onClickToDisabledOption"
          @unavailable="onClickToUnavailableOption(paymentOption.name)"
        />
        <setting-receiving-payments-yoo-money
          v-else-if="paymentOption.name === 'YOOMONEY'"
          :key="paymentOption.name"
          :option="paymentOption"
          @blocked="onClickToDisabledOption"
          @unavailable="onClickToUnavailableOption(paymentOption.name)"
        />
        <toggle-panel
          v-else
          :key="paymentOption.name"
          v-model="paymentOption.enabled"
          :title="paymentOption.displayName"
          :disabled="paymentOption.blocked"
          @click="handleToggleOption(paymentOption)"
        >
          <component :is="getIconComponent(paymentOption.name)" slot="icon" />
        </toggle-panel>
      </template>
    </substrate-mount>

    <cs-dialog v-model="isShownAccessInformation">
      <dialog-default-layout>
        <h2 slot="title" v-html="dialog.title"></h2>
        <p v-if="dialog.message" slot="content" v-text="dialog.message" />
        <div slot="footer" class="dialog-footer-container">
          <cs-button width="100%" @click="onCloseAccessInformation">
            {{ dialog.submit }}
          </cs-button>
        </div>
      </dialog-default-layout>
    </cs-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import HTTPService from '@/services/Http';

import {
  RUB_PAYMENT_OPTIONS,
  UZS_PAYMENT_OPTIONS
} from 'definitions/payment';

import CreditcardIcon from 'atoms/icons/finance/Creditcard';
import AppleIcon from 'atoms/icons/finance/Apple';
import GoogleIcon from 'atoms/icons/finance/Google';
import QiwiIcon from 'atoms/icons/finance/Qiwi';
import YooMoneyIcon from 'atoms/icons/finance/YooMoney';
import PayMeIcon from 'atoms/icons/finance/PayMe';
import YandexIcon from 'atoms/icons/finance/Yandex';

import TogglePanel from 'molecules/panels/TogglePanel';
import SubstrateMount from '@/components/capusta/SubstrateMount';

import DialogDefaultLayout from 'modules/Dialogs/DefaultLayout';

import SettingReceivingPaymentsQiwi from './components/SettingReceivingPaymentsQiwi';
import SettingReceivingPaymentsYooMoney from './components/SettingReceivingPaymentsYooMoney';

const PAYMENT_ICONS_MAP = {
  'CARD': CreditcardIcon,
  'APPLE': AppleIcon,
  'GOOGLE': GoogleIcon,
  'QIWI': QiwiIcon,
  'YOOMONEY': YooMoneyIcon,
  'PAYME': PayMeIcon,
  'YANDEX': YandexIcon,
};

export default {
  name: 'PaymentMethodsSettings',
  components: {
    TogglePanel,
    SubstrateMount,
    DialogDefaultLayout,
    SettingReceivingPaymentsQiwi,
    SettingReceivingPaymentsYooMoney,
  },
  created() {
    this.initializePaymentOptions();
  },
  data() {
    return {
      paymentOptions: [],
      isShownAccessInformation: false,
      dialog: {
        title: null,
        message: null,
        submit: null,
      },
    };
  },
  computed: {
    ...mapGetters(['getProjectPaymentOptions', 'getCurrency']),
  },
  methods: {
    setDialog({ title, message, submit }) {
      this.dialog.title = title;
      this.dialog.message = message;
      this.dialog.submit = submit;
    },
    getIconComponent(optionName) {
      return PAYMENT_ICONS_MAP[optionName];
    },
    // TODO: CPME-4044 Данный метод будет неактуален после выполнения задачи на бэкенде
    isUzsPaymentOption(optionName) {
      return UZS_PAYMENT_OPTIONS.includes(optionName);
    },
    // TODO: CPME-4044 Данный метод будет неактуален после выполнения задачи на бэкенде
    isRubPaymentOption(optionName) {
      return RUB_PAYMENT_OPTIONS.includes(optionName);
    },
    // TODO: CPME-4044 Данный метод будет неактуален после выполнения задачи на бэкенде
    isUnavailablePaymentOption(optionName) {
      const { code } = this.getCurrency;
      const isUnavailableUzsPaymentOption = this.isUzsPaymentOption(optionName) && code !== 'UZS';
      const isUnavailableRubPaymentOption = this.isRubPaymentOption(optionName) && code !== 'RUB';

      return isUnavailableUzsPaymentOption || isUnavailableRubPaymentOption;
    },
    initializePaymentOptions() {
      const preparedPaymentOptions = this.getProjectPaymentOptions
        .map(option => {
          return {
            ...option,
            displayName: this.$t(option.displayName),
            enabled: option.blocked ? false : option.enabled,
            unavailable: this.isUnavailablePaymentOption(option.name),
          };
        });

      this.paymentOptions = preparedPaymentOptions;
    },
    onClickToUnavailableOption(optionName) {
      const dialogData = this.isUzsPaymentOption(optionName) ? {
        title: this.$t('payment.methods.payme.not.allowed'),
        message: this.$t('payment.methods.payme.currency.info'),
        submit: this.$t('dialog.button.clear'),
      } : {
        title: this.$t('payment.methods.tokenized.and.wallet.not.allowed'),
        message: this.$t('payment.methods.tokenized.and.wallet.currency.info'),
        submit: this.$t('dialog.button.clear'),
      };

      this.setDialog(dialogData);
      this.isShownAccessInformation = true;
    },
    onClickToDisabledOption() {
      this.setDialog({
        title: this.$t('help.connect.payment.method'),
        message: null,
        submit: this.$t('common.close'),
      });

      this.isShownAccessInformation = true;
    },
    onCloseAccessInformation() {
      this.setDialog({
        title: null,
        message: null,
        submit: null,
      });

      this.isShownAccessInformation = false;
    },
    async handleToggleOption(option) {
      if (option.blocked) {
        this.onClickToDisabledOption();
        return;
      }

      if (option.unavailable) {
        this.onClickToUnavailableOption(option.name);
        return;
      }

      const toggledValue = !option.enabled;
      const type = option.name.toLowerCase();

      try {
        if (toggledValue) {
          await HTTPService.activatePaymentOption({}, type);
        } else {
          await HTTPService.disablePaymentOption(type);
        }

        option.enabled = toggledValue;

        this.$alert({
          type: 'success',
          text: 'сhanges.saved',
        });
      } catch (error) {
        console.log(error);

        this.$alert({
          type: 'warning',
          text: 'failed.to.change.payment.methods',
        });
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  a
    color: #9B51E0
    text-decoration: underline
    text-underline-position: under
    &:hover
      text-decoration: none

  .panel-list
    &:not(:last-child)
      margin-bottom: 10px

  .dialog-footer-container
    margin: 0 auto

    +respond-to('small')
      max-width: 230px
</style>
